import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ScoreColor = s =>
  !s ? 'var(--text-4)' :
  s < 2 ? 'var(--red)' :
  s < 3 ? 'var(--amber)' :
  s < 4 ? 'var(--blue)' : 'var(--green)';

const Maturity = s =>
  !s ? '—' : s < 2 ? 'Critical' : s < 3 ? 'Reactive' : s < 3.8 ? 'Developing' : s < 4.5 ? 'Structured' : 'High-Performing';

export default function Dashboard() {
  const navigate = useNavigate();
  const { authFetch, user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    authFetch('/api/companies').then(r => r.json())
      .then(d => { setCompanies(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [authFetch]);

  const totalResponses = companies.reduce((a, c) => a + (c.response_count || 0), 0);
  const scoredCos = companies.filter(c => c.last_score);
  const avgScore  = scoredCos.length
    ? (scoredCos.reduce((a, c) => a + c.last_score, 0) / scoredCos.length).toFixed(1) : null;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const kpis = [
    { label: 'Companies', value: companies.length, sub: 'in your portfolio', icon: '🏢' },
    { label: 'Total responses', value: totalResponses, sub: 'diagnostic submissions', icon: '📋' },
    { label: 'Portfolio score', value: avgScore || '—', sub: avgScore ? Maturity(parseFloat(avgScore)) : 'No assessments yet', icon: '◎', color: avgScore ? ScoreColor(parseFloat(avgScore)) : undefined, suffix: avgScore ? ' / 5' : '' },
    { label: 'AI credits', value: user?.credits ?? '—', sub: `${(user?.plan || 'free')} plan`, icon: '⚡' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-4)', marginBottom: 6 }}>
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
            <h1 className="page-title">
              {greeting}, <em className="page-title-em">{user?.name?.split(' ')[0] || 'there'}</em>
            </h1>
            <p className="page-sub">Your organizational intelligence overview.</p>
          </div>
          <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
            <button className="btn btn-outline btn-sm" onClick={() => navigate('/companies')}>+ New company</button>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/ai')}>Open AI workspace</button>
          </div>
        </div>
      </div>

      {/* Free plan banner */}
      {user?.plan === 'free' && (
        <div className="plan-banner">
          <div>
            <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 3, color: 'var(--blue)' }}>Upgrade to Pro</div>
            <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Unlock full diagnostics, AI reports, and 200 credits per month.</div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/billing')}>View plans →</button>
        </div>
      )}

      {/* KPI row */}
      <div className="g4" style={{ marginBottom: 32 }}>
        {kpis.map(k => (
          <div key={k.label} className="kpi-card">
            <div className="kpi-label">
              <span style={{ fontSize: 15 }}>{k.icon}</span>
              {k.label}
            </div>
            <div className="kpi-value" style={k.color ? { color: k.color } : {}}>
              {k.value}
              {k.suffix && <span style={{ fontSize: 16, color: 'var(--text-4)', fontWeight: 400 }}>{k.suffix}</span>}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-4)', fontWeight: 500 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Companies */}
      <div className="sec-header">
        <div className="sec-title">Companies</div>
        <div className="sec-line" />
        <span className="tag">{companies.length} total</span>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[1,2,3].map(i => <div key={i} className="skeleton skeleton-card" />)}
        </div>
      ) : companies.length === 0 ? (
        <div className="card empty-state">
          <div className="empty-icon">🏢</div>
          <div className="empty-title">Add your first company</div>
          <div className="empty-sub">Create a company workspace and share the diagnostic survey with your team to get started.</div>
          <button className="btn btn-primary" onClick={() => navigate('/companies')}>Create company</button>
        </div>
      ) : (
        <>
        <style>{'@media(max-width:640px){.co-table{display:none}.co-cards{display:flex!important}}@media(min-width:641px){.co-cards{display:none!important}}'}</style>
        <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 0 }}>
          <table className="table co-table" style={{ borderRadius: 0 }}>
            <thead>
              <tr>
                <th>Company</th>
                <th>Industry</th>
                <th>Responses</th>
                <th>Score</th>
                <th>Maturity</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {companies.map(c => (
                <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/report', { state: { companyId: c.id } })}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                      <div style={{
                        width: 34, height: 34, borderRadius: 'var(--radius)',
                        background: 'var(--blue)', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, fontWeight: 700, flexShrink: 0,
                      }}>
                        {(c.name || '?')[0].toUpperCase()}
                      </div>
                      <span style={{ fontWeight: 600, color: 'var(--text)', fontSize: 13.5 }}>{c.name}</span>
                    </div>
                  </td>
                  <td>{c.industry ? <span className="tag">{c.industry}</span> : <span style={{ color: 'var(--text-4)' }}>—</span>}</td>
                  <td>
                    <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: (c.response_count || 0) > 0 ? 'var(--text)' : 'var(--text-4)' }}>
                      {c.response_count || 0}
                    </span>
                  </td>
                  <td>
                    {c.last_score ? (
                      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: 15, color: ScoreColor(c.last_score) }}>
                        {c.last_score.toFixed(1)}
                      </span>
                    ) : <span style={{ color: 'var(--text-4)' }}>—</span>}
                  </td>
                  <td>
                    {c.last_score ? (
                      <span className={`badge ${c.last_score >= 4 ? 'badge-green' : c.last_score >= 3 ? 'badge-blue' : c.last_score >= 2 ? 'badge-amber' : 'badge-red'}`}>
                        {Maturity(c.last_score)}
                      </span>
                    ) : <span className="badge badge-neutral">No data</span>}
                  </td>
                  <td>
                    <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); navigate('/report', { state: { companyId: c.id } }); }}>
                      View →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Mobile card view */}
        <div className="co-cards" style={{ display:'none', flexDirection:'column', gap:1, background:'var(--bg-surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden', marginBottom: 28 }}>
          {companies.map(c => (
            <div key={c.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'13px 16px', borderBottom:'1px solid var(--border)', cursor:'pointer' }}
              onClick={() => navigate('/report', { state: { companyId: c.id } })}>
              <div style={{ width:32, height:32, borderRadius:'var(--radius)', background:'var(--blue)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, flexShrink:0 }}>
                {(c.name||'?')[0].toUpperCase()}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:600, fontSize:13.5, color:'var(--text)', marginBottom:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.name}</div>
                <div style={{ fontSize:12, color:'var(--text-4)' }}>{c.response_count||0} responses</div>
              </div>
              {c.last_score && <span style={{ fontWeight:700, fontSize:15, color: c.last_score>=4?'var(--green)':c.last_score>=3?'var(--blue)':c.last_score>=2?'var(--amber)':'var(--red)', fontVariantNumeric:'tabular-nums', flexShrink:0 }}>{c.last_score.toFixed(1)}</span>}
              <span style={{ color:'var(--text-4)', fontSize:14, flexShrink:0 }}>›</span>
            </div>
          ))}
        </div>
        </>
      )}

      {/* Quick actions */}
      {companies.length > 0 && (
        <>
          <div className="sec-header">
            <div className="sec-title">Quick actions</div>
            <div className="sec-line" />
          </div>
          <div className="g3" style={{ gap: 12 }}>
            {[
              { icon: '🤖', label: 'AI Workspace', sub: 'Ask questions, build frameworks, generate artifacts from your data', page: '/ai', primary: true },
              { icon: '📥', label: 'Import audit', sub: 'Upload McKinsey, KPMG, or internal reports for AI-powered analysis', page: '/imports' },
              { icon: '📄', label: 'Knowledge base', sub: 'Upload SOPs, policies, and org documents for the AI to reference', page: '/documents' },
            ].map(a => (
              <div key={a.page} className={`card ${a.primary ? 'card-accent' : ''}`} style={{ cursor: 'pointer', padding: '18px 20px' }}
                onClick={() => navigate(a.page)}>
                <div style={{ fontSize: 22, marginBottom: 10 }}>{a.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', marginBottom: 5 }}>{a.label}</div>
                <div style={{ fontSize: 13, color: 'var(--text-3)', lineHeight: 1.55 }}>{a.sub}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
